import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NotificationService, NotificationLog } from '../../services/notification.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-notification-logs',
  templateUrl: './notification-logs.component.html',
  styleUrls: ['./notification-logs.component.css']
})
export class NotificationLogsComponent implements OnInit {
  logs: NotificationLog[] = [];
  statistics: any = {};
  loading = false;
  currentPage = 1;
  totalPages = 1;
  perPage = 25;
  selectedLog: NotificationLog | null = null;
  searchTerm = '';
  statusTabs: Array<{ label: string; value: string | null }> = [
    { label: 'Todos', value: null },
    { label: 'Enviados', value: 'sent' },
    { label: 'Pendientes', value: 'pending' },
    { label: 'Fallidos', value: 'failed' }
  ];
  activeStatusValue: string | null = null;

  filterForm: FormGroup;
  showStatistics = true;

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {
    this.filterForm = this.fb.group({
      type: [''],
      status: [''],
      user_id: [''],
      configuration_id: [''],
      date_from: [''],
      date_to: ['']
    });
  }

  ngOnInit(): void {
    this.loadLogs();
    this.loadStatistics();
  }

  loadLogs(page: number = 1): void {
    this.loading = true;
    const filters = this.filterForm.value;
    const params = {
      page: page.toString(),
      per_page: this.perPage.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
      )
    };

    this.notificationService.getNotificationLogs(params).subscribe({
      next: (response) => {
        this.logs = response.data.data || [];
        this.currentPage = response.data.current_page || 1;
        this.totalPages = response.data.last_page || 1;
        this.syncSelectedLog();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading logs:', error);
        this.loading = false;
        Swal.fire('Error', 'No se pudieron cargar los logs', 'error');
      }
    });
  }

  loadStatistics(): void {
    const filters = this.filterForm.value;
    const params = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
    );

    this.notificationService.getNotificationStatistics(params).subscribe({
      next: (response) => {
        this.statistics = response.data || {};
      },
      error: (error) => {
        console.error('Error loading statistics:', error);
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadLogs(1);
    this.loadStatistics();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.activeStatusValue = null;
    this.onFilterChange();
  }

  selectStatusTab(tabValue: string | null): void {
    this.activeStatusValue = tabValue;
    this.filterForm.patchValue({ status: tabValue || '' });
    this.onFilterChange();
  }

  onSearch(term: string): void {
    this.searchTerm = term.trim().toLowerCase();
  }

  get filteredLogs(): NotificationLog[] {
    if (!this.searchTerm) {
      return this.logs;
    }

    return this.logs.filter((log) => {
      const haystack = `${log.title} ${log.message}`.toLowerCase();
      return haystack.includes(this.searchTerm);
    });
  }

  selectLog(log: NotificationLog): void {
    this.selectedLog = log;
  }

  private syncSelectedLog(): void {
    if (!this.logs.length) {
      this.selectedLog = null;
      return;
    }

    if (this.selectedLog) {
      const match = this.logs.find((log) => log.id === this.selectedLog?.id);
      if (match) {
        this.selectedLog = match;
        return;
      }
    }

    this.selectedLog = this.logs[0];
  }

  getStatusChipClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'status-chip success';
      case 'failed':
        return 'status-chip danger';
      case 'pending':
        return 'status-chip warning';
      default:
        return 'status-chip neutral';
    }
  }

  getTypeChipClass(type: string): string {
    switch (type) {
      case 'push':
        return 'type-chip push';
      case 'whatsapp':
        return 'type-chip whatsapp';
      default:
        return 'type-chip default';
    }
  }

  getFormattedDate(date: string | undefined): string {
    if (!date) {
      return 'Sin fecha';
    }

    return new Date(date).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  retryFailedNotifications(): void {
    Swal.fire({
      title: '¿Reintentar notificaciones fallidas?',
      text: 'Se reintentarán todas las notificaciones con estado "fallido"',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, reintentar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.notificationService.retryFailedNotifications().subscribe({
          next: (response) => {
            Swal.fire('Éxito', response.message, 'success');
            this.loadLogs(this.currentPage);
            this.loadStatistics();
          },
          error: (error) => {
            console.error('Error retrying notifications:', error);
            Swal.fire('Error', 'No se pudieron reintentar las notificaciones', 'error');
          }
        });
      }
    });
  }

  cleanupOldLogs(): void {
    Swal.fire({
      title: 'Limpiar logs antiguos',
      input: 'number',
      inputLabel: 'Días a mantener',
      inputValue: 30,
      inputAttributes: {
        min: '1',
        max: '365'
      },
      showCancelButton: true,
      confirmButtonText: 'Limpiar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value || parseInt(value) < 1) {
          return 'Debe ingresar un número válido mayor a 0';
        }
        return null;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const days = parseInt(result.value);
        this.notificationService.cleanupNotificationLogs(days).subscribe({
          next: (response) => {
            Swal.fire('Éxito', response.message, 'success');
            this.loadLogs(this.currentPage);
            this.loadStatistics();
          },
          error: (error) => {
            console.error('Error cleaning up logs:', error);
            Swal.fire('Error', 'No se pudieron limpiar los logs', 'error');
          }
        });
      }
    });
  }

  viewLogDetails(log: NotificationLog): void {
    Swal.fire({
      title: 'Detalles de la Notificación',
      html: `
        <div class="text-left">
          <p><strong>ID:</strong> ${log.id}</p>
          <p><strong>Tipo:</strong> ${log.type}</p>
          <p><strong>Estado:</strong> ${log.status}</p>
          <p><strong>Título:</strong> ${log.title}</p>
          <p><strong>Mensaje:</strong> ${log.message}</p>
          ${log.recipient_phone ? `<p><strong>Teléfono:</strong> ${log.recipient_phone}</p>` : ''}
          ${log.error_message ? `<p><strong>Error:</strong> ${log.error_message}</p>` : ''}
          <p><strong>Creado:</strong> ${new Date(log.created_at).toLocaleString()}</p>
          ${log.sent_at ? `<p><strong>Enviado:</strong> ${new Date(log.sent_at).toLocaleString()}</p>` : ''}
          ${log.metadata ? `<p><strong>Metadata:</strong> <pre>${JSON.stringify(log.metadata, null, 2)}</pre></p>` : ''}
        </div>
      `,
      width: '600px',
      confirmButtonText: 'Cerrar'
    });
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.loadLogs(page);
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'sent': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'pending': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getTypeBadgeClass(type: string): string {
    switch (type) {
      case 'push': return 'bg-primary';
      case 'whatsapp': return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'sent': return 'Enviado';
      case 'failed': return 'Fallido';
      case 'pending': return 'Pendiente';
      default: return status;
    }
  }

  getTypeText(type: string): string {
    switch (type) {
      case 'push': return 'Push';
      case 'whatsapp': return 'WhatsApp';
      default: return type;
    }
  }

  toggleStatistics(): void {
    this.showStatistics = !this.showStatistics;
  }
}
